import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Shield, LogOut, User, Mail, Briefcase, Users as UsersIcon, DollarSign, Calendar, BarChart3, Trophy, AlertTriangle, Sparkles } from 'lucide-react';

const Dashboard = () => {
  const { userProfile, signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'admin':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'tesorero':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'entrenador':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'visualizador':
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const getRoleLabel = (role) => {
    switch(role) {
      case 'admin':
        return 'Administrador';
      case 'tesorero':
        return 'Tesorero';
      case 'entrenador':
        return 'Entrenador';
      case 'visualizador':
        return 'Visualizador';
      default:
        return role;
    }
  };

  // Módulos disponibles según el rol
  const modules = [
    {
      name: 'Panel del Entrenador',
      description: 'Gestión completa: jugadores, entrenamientos, partidos, alertas e informes',
      icon: UsersIcon,
      gradient: 'from-red-500 to-rose-600',
      path: '/coach',
      roles: ['admin', 'entrenador'],
      featured: true,
      pattern: 'dots'
    },
    {
      name: 'Gestión Financiera',
      description: 'Control de ingresos, gastos y cuotas',
      icon: DollarSign,
      gradient: 'from-emerald-500 to-green-600',
      path: '/finance',
      roles: ['admin', 'tesorero'],
      pattern: 'grid'
    },
    {
      name: 'Reportes',
      description: 'Estadísticas y análisis del club',
      icon: BarChart3,
      gradient: 'from-amber-500 to-orange-600',
      path: '/reports',
      roles: ['admin', 'tesorero', 'visualizador'],
      pattern: 'lines'
    },
    {
      name: 'Gestión de Usuarios',
      description: 'Administrar usuarios del sistema',
      icon: Shield,
      gradient: 'from-purple-500 to-violet-600',
      path: '/users',
      roles: ['admin'],
      pattern: 'circles'
    }
  ];

  const availableModules = modules.filter(module =>
    module.roles.includes(userProfile?.role)
  );

  // Patrones de fondo para las tarjetas
  const getPattern = (patternType) => {
    switch(patternType) {
      case 'dots':
        return 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)';
      case 'grid':
        return 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)';
      case 'lines':
        return 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.05) 10px, rgba(255,255,255,0.05) 20px)';
      case 'circles':
        return 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.05) 0%, transparent 50%)';
      default:
        return 'none';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
      {/* Efectos de luz de fondo más alegres */}
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-red-600/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-rose-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-red-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>

      {/* Header alegre y moderno */}
      <header className="relative border-b border-white/5 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              {/* Logo con efecto brillante */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-rose-600 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <div className="relative bg-white rounded-2xl p-3 shadow-2xl transform group-hover:scale-105 transition-transform">
                  <img
                    src="https://files.fcf.cat/escudos/clubes/escudos/00100_0001111944_pd.jpg"
                    alt="FC Puigdalber Logo"
                    className="w-12 h-12 object-contain"
                  />
                </div>
              </div>

              <div>
                <h1 className="text-3xl font-black text-white mb-1 bg-gradient-to-r from-white to-white/80 bg-clip-text">
                  FC Puigdalber
                </h1>
                <p className="text-white/50 text-sm font-medium">Sistema de Gestión</p>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="group flex items-center gap-3 px-5 py-3 bg-white/5 hover:bg-red-600 text-white/70 hover:text-white rounded-xl transition-all duration-300 border border-white/10 hover:border-red-500"
            >
              <LogOut className="w-4 h-4 group-hover:rotate-12 transition-transform" />
              <span className="font-medium">Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Card - Más alegre */}
        <div className="relative mb-12 overflow-hidden">
          {/* Fondo con patrón */}
          <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 via-rose-600/10 to-red-600/10 rounded-3xl"></div>
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }}></div>

          <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-10 shadow-2xl">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="w-6 h-6 text-red-500 animate-pulse" />
                  <span className="text-red-500 font-bold text-sm uppercase tracking-wider">Bienvenido</span>
                </div>
                <h2 className="text-5xl font-black text-white mb-3 leading-tight">
                  ¡Hola, <span className="bg-gradient-to-r from-red-500 to-rose-600 bg-clip-text text-transparent">{userProfile?.full_name}</span>!
                </h2>
                <p className="text-white/60 text-lg font-medium">
                  Selecciona un módulo para comenzar a gestionar el club
                </p>
              </div>

              <div className={`px-6 py-3 rounded-2xl border-2 font-bold ${getRoleBadgeColor(userProfile?.role)} backdrop-blur-sm`}>
                {getRoleLabel(userProfile?.role)}
              </div>
            </div>
          </div>
        </div>

        {/* Módulos disponibles - Grid dinámico */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-8">
            <h3 className="text-2xl font-black text-white">Módulos Disponibles</h3>
            <div className="h-px flex-1 bg-gradient-to-r from-white/20 to-transparent"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {availableModules.map((module, index) => {
              const Icon = module.icon;
              return (
                <button
                  key={module.name}
                  onClick={() => navigate(module.path)}
                  className={`group relative bg-white/[0.03] border border-white/10 rounded-2xl p-8 hover:bg-white/[0.05] transition-all duration-500 text-left overflow-hidden ${
                    module.featured ? 'md:col-span-2 md:row-span-1' : ''
                  }`}
                  style={{
                    transitionDelay: `${index * 100}ms`,
                  }}
                >
                  {/* Patrón de fondo */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      backgroundImage: getPattern(module.pattern),
                      backgroundSize: '20px 20px'
                    }}
                  ></div>

                  {/* Gradiente de hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${module.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

                  {/* Borde animado */}
                  <div className={`absolute inset-0 rounded-2xl border-2 border-transparent bg-gradient-to-r ${module.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} style={{ WebkitMaskComposite: 'xor', maskComposite: 'exclude', WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', padding: '2px' }}></div>

                  <div className="relative flex items-start gap-6">
                    {/* Icono con gradiente */}
                    <div className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${module.gradient} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-xl`}>
                      <Icon className="w-8 h-8 text-white" />
                      <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${module.gradient} blur-xl opacity-50 group-hover:opacity-75 transition-opacity`}></div>
                    </div>

                    <div className="flex-1">
                      <h4 className="text-2xl font-bold text-white mb-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-white group-hover:to-white/70 transition-all">
                        {module.name}
                      </h4>
                      <p className="text-white/60 font-medium leading-relaxed">
                        {module.description}
                      </p>

                      {/* Indicador de acción */}
                      <div className="mt-4 flex items-center gap-2 text-white/40 group-hover:text-red-500 transition-colors">
                        <span className="text-sm font-bold">Acceder</span>
                        <svg className="w-4 h-4 group-hover:translate-x-2 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* User Info Card - Más alegre */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/[0.02] rounded-2xl"></div>
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(255,255,255,0.03) 20px, rgba(255,255,255,0.03) 40px)',
          }}></div>

          <div className="relative bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">Tu Cuenta</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="group flex items-center gap-4 p-5 bg-white/[0.03] hover:bg-white/[0.05] rounded-xl transition-all border border-white/5 hover:border-red-500/30">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Mail className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-1">Email</p>
                  <p className="font-bold text-white">{userProfile?.email}</p>
                </div>
              </div>

              <div className="group flex items-center gap-4 p-5 bg-white/[0.03] hover:bg-white/[0.05] rounded-xl transition-all border border-white/5 hover:border-red-500/30">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-600/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Briefcase className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-1">Rol</p>
                  <p className="font-bold text-white">{getRoleLabel(userProfile?.role)}</p>
                </div>
              </div>

              {userProfile?.team && (
                <div className="group flex items-center gap-4 p-5 bg-white/[0.03] hover:bg-white/[0.05] rounded-xl transition-all border border-white/5 hover:border-red-500/30">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <UsersIcon className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-1">Equipo</p>
                    <p className="font-bold text-white">{userProfile.team}</p>
                  </div>
                </div>
              )}

              <div className="group flex items-center gap-4 p-5 bg-white/[0.03] hover:bg-white/[0.05] rounded-xl transition-all border border-white/5 hover:border-red-500/30">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500/20 to-green-600/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Shield className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-1">Estado</p>
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                    <p className="font-bold text-green-400">
                      {userProfile?.active ? 'Activo' : 'Inactivo'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
